// AudioPlayer - play/stop an <audio> element from a [data-toggle] trigger.
// Behavioral port of v1 modules/AudioPlayer.js; the HTML contract
// (data-audioplayer on the media element, a Toggle trigger pointing at it) is
// unchanged, because the consumer really renders it - the audiobook preview on
// the product detail page (Catalog/element/Utils/audiobook_preview.php).
//
// The module owns no UI of its own: the button is a plain Toggle trigger, and
// swapping its icon and label is what tells the user whether the preview is
// playing. All this module does is turn that toggle into playback - and, unlike
// v1, keep the two in sync in the other direction too. See docs/porting.md for
// the v1 bugs fixed along the way.
//
// HTML contract:
//   <button data-toggle="self, #audio-preview"
//           data-toggle-icon="pause" data-toggle-text="Zastaviť">
//     <i class="far fa-play"></i>
//     <span data-toggle-text-target>Prehrať ukážku</span>
//   </button>
//   <audio id="audio-preview" data-audioplayer>
//     <source type="audio/mp3" src="...">
//   </audio>

import Toggle, { TOGGLE_EVT, type ToggleEventDetail } from './toggle';

export interface AudioPlayerConfig {
  /** Class Toggle swaps on the trigger; must match the Toggle config. */
  toggleClass?: string;
  /** Rewind to the start when playback stops - the v1 „Zastaviť" semantics. */
  rewindOnStop?: boolean;
  /** Stop every other player on the page when this one starts. */
  exclusive?: boolean;
}

export interface AudioPlayerChangeDetail {
  playing: boolean;
}

/** Fired on the media element after playback started or stopped. */
export const AUDIO_PLAYER_CHANGE_EVT = 'martinus:audio-player:change';

const defaultConfig: AudioPlayerConfig = {
  toggleClass: 'is-active',
  rewindOnStop: true,
  exclusive: true,
};

/** HTMLMediaElement.NETWORK_NO_SOURCE - resource selection found nothing usable. */
const NETWORK_NO_SOURCE = 3;

const instances = new WeakMap<HTMLMediaElement, AudioPlayerInstance>();

/** Live instances, so `exclusive` can reach the other players on the page. */
const live = new Set<AudioPlayerInstance>();

/** One media element. */
export class AudioPlayerInstance {
  element: HTMLMediaElement;

  config: AudioPlayerConfig;

  /** Trigger driving this player; the last one that toggled it wins. */
  private trigger: HTMLElement | null = null;

  private triggerResolved = false;

  private wasPlaying = false;

  constructor(element: HTMLMediaElement, config: AudioPlayerConfig = {}) {
    this.element = element;
    this.config = { ...defaultConfig, ...config };

    this._init();
  }

  get playing(): boolean {
    return !this.element.paused && !this.element.ended && !this.failed;
  }

  /**
   * A source the browser cannot use neither pauses the element nor settles the
   * play() promise: `paused` stays false and nothing ever plays. Going by
   * `paused` alone would leave the trigger on „Zastaviť" over silence forever,
   * so catching the rejected promise is not enough on its own.
   */
  private get failed(): boolean {
    return Boolean(this.element.error) || this.element.networkState === NETWORK_NO_SOURCE;
  }

  play(): void {
    const started = this.element.play?.();

    // play() rejects when the browser refuses playback outright (autoplay
    // policy, or pause() arriving before playback began); v1 ignored the
    // promise, so the trigger sat there saying „Zastaviť" over silence. The
    // unusable-source case never settles this promise at all - that one is
    // handled by `failed` and the error listener. Old browsers - and jsdom -
    // return undefined instead of a promise.
    if (started && typeof started.catch === 'function') {
      started.catch(() => this._sync());
    }
  }

  pause(): void {
    this.element.pause?.();
  }

  /** Pause and rewind - what the trigger's „Zastaviť" label promises. */
  stop(): void {
    this.pause();

    if (this.config.rewindOnStop !== false) {
      this.element.currentTime = 0;
    }
  }

  toggle(): void {
    if (this.playing) {
      this.stop();
    } else {
      this.play();
    }
  }

  /** Re-resolve the trigger (call after the surrounding markup changed). */
  update(): void {
    this.trigger = null;
    this.triggerResolved = false;
    this._sync();
  }

  destroy(): void {
    live.delete(this);

    this.element.removeEventListener(TOGGLE_EVT, this._onToggle);
    this.element.removeEventListener('play', this._onStateChange);
    this.element.removeEventListener('pause', this._onStateChange);
    this.element.removeEventListener('ended', this._onStateChange);
    this.element.removeEventListener('error', this._onStateChange, true);

    this.trigger = null;
    this.triggerResolved = false;
  }

  private _init(): void {
    this.wasPlaying = this.playing;

    this.element.addEventListener(TOGGLE_EVT, this._onToggle);

    // v1 only listened for `ended`. Listening for play/pause instead covers
    // every way playback can stop - the spec fires `pause` before `ended`, so
    // this handles natural end, native controls, media keys and another player
    // taking over, all of which used to leave the trigger stuck mid-state.
    this.element.addEventListener('play', this._onStateChange);
    this.element.addEventListener('pause', this._onStateChange);
    this.element.addEventListener('ended', this._onStateChange);
    // Capture: an `error` on a child <source> does not bubble.
    this.element.addEventListener('error', this._onStateChange, true);

    live.add(this);
  }

  private _onToggle = (e: Event): void => {
    const { detail } = e as CustomEvent<ToggleEventDetail>;

    // The event bubbles, so only react to this element's own toggle.
    if (detail?.target !== this.element) {
      return;
    }

    if (detail.trigger instanceof HTMLElement) {
      this.trigger = detail.trigger;
      this.triggerResolved = true;
    }

    this.toggle();
  };

  private _onStateChange = (): void => {
    const { playing } = this;

    // Hooked to the event rather than to play(), so it also covers playback
    // started from the native controls or from the consumer's own code.
    if (playing && this.config.exclusive !== false) {
      live.forEach(instance => {
        if (instance !== this) instance.stop();
      });
    }

    this._sync();

    if (playing === this.wasPlaying) {
      return;
    }

    this.wasPlaying = playing;

    this.element.dispatchEvent(
      new CustomEvent<AudioPlayerChangeDetail>(AUDIO_PLAYER_CHANGE_EVT, {
        bubbles: true,
        detail: { playing },
      }),
    );
  };

  /** Bring the trigger and the element's own toggle class back in line. */
  private _sync(): void {
    const { playing } = this;
    const trigger = this._getTrigger();

    if (trigger) {
      const className = this._toggleClass(trigger);

      // Only when the two actually disagree - on a click Toggle has already
      // flipped the trigger and re-running doToggle would flip it back.
      if (Toggle.isToggled(trigger, className) !== playing) {
        Toggle.doToggle({
          target: trigger,
          className,
          icon: trigger.getAttribute('data-toggle-icon'),
          iconTarget: trigger.getAttribute('data-toggle-icon-target'),
          text: trigger.getAttribute('data-toggle-text'),
          expand: trigger.hasAttribute('data-toggle-expand'),
          // As in v1: this is us restoring the trigger, not a user toggle.
          dispatchEvent: false,
        });
      }
    }

    // The trigger typically targets `self, #player`, so Toggle marks the media
    // element as active too. v1 never took that class back off, leaving a
    // finished player flagged as playing for anything styling or querying it.
    this.element.classList.toggle(this._toggleClass(this.element), playing);
  }

  private _toggleClass(element: Element): string {
    return (
      element.getAttribute('data-toggle-class') ||
      this.config.toggleClass ||
      (defaultConfig.toggleClass as string)
    );
  }

  /**
   * The trigger is normally learned from the toggle event. Until then - the
   * player may start from native controls or from code - find it in the DOM.
   * v1 instead took the first <button> in the player's parent, which only
   * worked because the consumer's markup happens to have exactly one.
   */
  private _getTrigger(): HTMLElement | null {
    if (this.triggerResolved) {
      return this.trigger;
    }

    this.triggerResolved = true;
    this.trigger =
      [...document.querySelectorAll<HTMLElement>('[data-toggle]')].find(trigger =>
        (trigger.getAttribute('data-toggle') || '')
          .split(',')
          .some(part => this._pointsAtElement(trigger, part.trim())),
      ) ?? null;

    return this.trigger;
  }

  /** Does one entry of a trigger's data-toggle list resolve to this player? */
  private _pointsAtElement(trigger: Element, selector: string): boolean {
    switch (selector) {
      case '':
      case 'self':
      case 'parent':
        return false;
      case 'previous':
        return trigger.previousElementSibling === this.element;
      case 'next':
        return trigger.nextElementSibling === this.element;
      default:
        try {
          return this.element.matches(selector);
        } catch {
          return false;
        }
    }
  }
}

export default class AudioPlayer {
  selector: string;

  config: AudioPlayerConfig;

  /** v1 held the elements here; v2 holds the instances. */
  players: AudioPlayerInstance[] = [];

  constructor(selector = '[data-audioplayer]', config: AudioPlayerConfig = {}) {
    this.selector = selector;
    this.config = config;

    this.players = this._initialize();
  }

  /** The player belonging to a media element, if it was initialized. */
  static getInstance(element: HTMLMediaElement | null): AudioPlayerInstance | null {
    return (element && instances.get(element)) || null;
  }

  update(): void {
    this.destroy();
    this.players = this._initialize();
  }

  destroy(): void {
    this.players.forEach(player => {
      instances.delete(player.element);
      player.destroy();
    });
    this.players = [];
  }

  private _initialize(): AudioPlayerInstance[] {
    return [...document.querySelectorAll(this.selector)].reduce<AudioPlayerInstance[]>(
      (created, element) => {
        // v1 called play()/pause() on whatever carried the attribute.
        if (!(element instanceof HTMLMediaElement)) {
          return created;
        }

        const instance = new AudioPlayerInstance(element, this.config);
        instances.set(element, instance);
        created.push(instance);

        return created;
      },
      [],
    );
  }
}
